import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppButton from '../../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../../components/feature/ScrollToTopButton';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const SCHEMA_SEGURANCA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      '@id': `${import.meta.env.VITE_SITE_URL}/servicos/seguranca-trabalho#service`,
      name: 'Engenharia de Segurança do Trabalho — PGR, LTCAT e NRs',
      description: 'Elaboração de PGR, LTCAT, laudos de insalubridade e periculosidade, PPP, auditorias e treinamentos conforme Normas Regulamentadoras em Sorocaba e Campinas.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Engeporto Engenharia e Serviços Ltda.',
        url: import.meta.env.VITE_SITE_URL,
      },
      areaServed: ['Sorocaba', 'Campinas', 'Porto Feliz'],
      serviceType: 'Engenharia de Segurança do Trabalho',
      url: `${import.meta.env.VITE_SITE_URL}/servicos/seguranca-trabalho`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: import.meta.env.VITE_SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Serviços', item: `${import.meta.env.VITE_SITE_URL}/servicos` },
        { '@type': 'ListItem', position: 3, name: 'Segurança do Trabalho', item: `${import.meta.env.VITE_SITE_URL}/servicos/seguranca-trabalho` },
      ],
    },
  ],
});

const SegurancaTrabalhoPage = () => {
  useEffect(() => {
    document.title = 'Segurança do Trabalho Sorocaba Campinas | PGR, LTCAT e NRs — Engeporto';
    const setMeta = (name: string, content: string, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      const el = document.querySelector(sel) as HTMLMetaElement | null;
      if (el) el.setAttribute('content', content);
    };
    setMeta('description', 'Elaboração de PGR, LTCAT, laudos de insalubridade e periculosidade, PPP e treinamentos conforme Normas Regulamentadoras nas Regiões Metropolitanas de Sorocaba e Campinas. Engeporto Engenharia.');
    setMeta('keywords', 'segurança do trabalho Sorocaba, PGR Campinas, LTCAT, laudo insalubridade, NR-01, NR-06, NR-10, NR-35');
    setMeta('og:title', 'Segurança do Trabalho | PGR, LTCAT e NRs — Engeporto Sorocaba Campinas', true);
    setMeta('og:description', 'PGR, LTCAT, laudos de insalubridade e treinamentos NRs em Sorocaba e Campinas.', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/servicos/seguranca-trabalho`, true);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/servicos/seguranca-trabalho`);
    return () => { canonical?.remove(); };
  }, []);

  const servicos = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Elaboração de PGR',
      description: 'Programa de Gerenciamento de Riscos conforme NR-01, incluindo inventário de riscos e plano de ação.',
    },
    {
      icon: 'ri-sound-module-line',
      title: 'LTCAT',
      description: 'Laudo Técnico das Condições Ambientais do Trabalho para caracterização de agentes nocivos.',
    },
    {
      icon: 'ri-alert-line',
      title: 'Laudos de Insalubridade e Periculosidade',
      description: 'Avaliação técnica de agentes insalubres e periculosos para fins trabalhistas.',
    },
    {
      icon: 'ri-profile-line',
      title: 'PPP - Perfil Profissiográfico Previdenciário',
      description: 'Elaboração de PPP para comprovação de exposição a agentes nocivos junto ao INSS.',
    },
    {
      icon: 'ri-book-open-line',
      title: 'Programas e Laudos conforme NRs',
      description: 'Elaboração de programas técnicos conforme NR-06, NR-07, NR-09, NR-10, NR-12, NR-17, NR-23.',
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Análise de Riscos Ocupacionais',
      description: 'Identificação, avaliação e controle de riscos físicos, químicos, biológicos e ergonômicos.',
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Gestão de EPI e EPC',
      description: 'Especificação técnica de Equipamentos de Proteção Individual e Coletiva.',
    },
    {
      icon: 'ri-file-damage-line',
      title: 'Investigação e Análise de Acidentes',
      description: 'Investigação técnica de acidentes de trabalho e identificação de causas raízes.',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Treinamentos de Segurança do Trabalho',
      description: 'Capacitações técnicas conforme NRs, incluindo NR-10, NR-35, NR-33, NR-20.',
    },
    {
      icon: 'ri-file-search-line',
      title: 'Auditorias Internas',
      description: 'Auditorias de conformidade legal e verificação de atendimento às NRs.',
    },
    {
      icon: 'ri-team-line',
      title: 'Implantação de Cultura de Prevenção',
      description: 'Desenvolvimento de programas comportamentais e campanhas de conscientização.',
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: 'Compliance Ocupacional',
      description: 'Adequação normativa completa e gestão de documentação legal.',
    },
  ];

  const nrs = [
    'NR-01 - Disposições Gerais e PGR',
    'NR-06 - Equipamentos de Proteção Individual',
    'NR-07 - PCMSO',
    'NR-09 - Avaliação e Controle de Riscos',
    'NR-10 - Segurança em Instalações Elétricas',
    'NR-12 - Segurança em Máquinas',
    'NR-17 - Ergonomia',
    'NR-23 - Proteção Contra Incêndios',
  ];

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: SCHEMA_SEGURANCA }}
      />
      <Navbar />
      
      <section className="pt-36 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-[#0A1628] via-[#0D1B33] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/servicos"
              className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-4 md:mb-8 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar para Serviços
            </Link>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-bold mb-6">
                  <i className="ri-shield-check-line text-lg"></i>
                  PILAR 2
                </div>
                
                <h1 className="font-display font-bold text-2xl md:text-5xl lg:text-6xl text-white mb-4 md:mb-6 leading-tight">
                  Engenharia de Segurança do Trabalho
                </h1>
                
                <p className="text-sm md:text-xl text-gray-300 leading-relaxed">
                  Programas, laudos técnicos e consultoria especializada em gestão de riscos ocupacionais e conformidade com Normas Regulamentadoras
                </p>
              </div>

              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/dcce69b53a27b1d7cd14219d010f518d.jpg"
                  alt="Segurança do Trabalho"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-16"
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4">
              Serviços Especializados
            </h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
              Soluções completas em segurança e saúde ocupacional para garantir conformidade e proteção aos trabalhadores e empregadores
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {servicos.map((servico, index) => (
              <motion.div
                key={servico.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                className="bg-gray-50 rounded-xl p-5 md:p-8 hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl mb-4 md:mb-6">
                  <i className={`${servico.icon} text-xl md:text-2xl text-[#D4AF37]`}></i>
                </div>
                <h3 className="font-display font-bold text-base md:text-xl text-[#0A1628] mb-3">
                  {servico.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {servico.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://readdy.ai/api/search-image?query=safety%20engineer%20conducting%20workplace%20inspection%20with%20tablet%20and%20clipboard%20checking%20safety%20equipment%20and%20procedures%20in%20industrial%20facility%20professional%20wearing%20hard%20hat%20and%20safety%20vest%20bright%20modern%20environment&width=800&height=600&seq=seg002&orientation=landscape"
                alt="Conformidade NRs"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4 md:mb-6">
                Conformidade com Normas Regulamentadoras
              </h2>
              <p className="text-sm md:text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
                Garantimos o atendimento completo às principais NRs aplicáveis ao seu negócio, protegendo sua empresa de autuações e garantindo a segurança dos colaboradores.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {nrs.map((nr) => (
                  <div key={nr} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#D4AF37] rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{nr}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-16"
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4">
              Por Que Investir em Segurança do Trabalho?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Proteção Legal',
                description: 'Evite multas e autuações por descumprimento de normas',
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Saúde dos Colaboradores',
                description: 'Preserve a integridade física e mental da equipe',
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Produtividade',
                description: 'Reduza afastamentos e aumente a eficiência operacional',
              },
              {
                icon: 'ri-award-line',
                title: 'Reputação',
                description: 'Demonstre responsabilidade social e compromisso com pessoas',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl mx-auto mb-4 md:mb-6">
                  <i className={`${item.icon} text-2xl md:text-3xl text-[#D4AF37]`}></i>
                </div>
                <h3 className="font-display font-bold text-base md:text-xl text-[#0A1628] mb-2 md:mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-gradient-to-br from-[#0A1628] via-[#0D1B33] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-4 md:mb-6">
              Precisa Regularizar sua Empresa?
            </h2>
            <p className="text-sm md:text-xl text-gray-300 mb-6 md:mb-10">
              Fale com nossos especialistas em segurança do trabalho
            </p>
            <a
              href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Tenho interesse em serviços de Segurança do Trabalho (PGR, LTCAT, laudos). Gostaria de solicitar um orçamento.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A1628] px-6 py-4 md:px-10 md:py-5 rounded-lg text-base md:text-lg font-bold hover:bg-[#C4A030] transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl md:text-2xl"></i>
              Solicitar Orçamento
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default SegurancaTrabalhoPage;